import { isset, wait } from '.';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const isEqual = require('lodash.isequal');
const url = require('url');

const VALUE_SEPARATOR = ',';

export default {
    /**
     * Update browser's URL hash without page reload
     *
     * @param {string} newHash If not set, hash is removed from URL
     */
    setHashHistory(newHash) {
        const formattedHash = newHash
            ? `#${newHash}`
            : window.location.pathname + window.location.search;

        if (window.history.pushState) {
            window.history.pushState(null, null, formattedHash);
        } else {
            window.location.hash = formattedHash;
        }
    },

    /**
     * Parse URL hash, either from the browser's current URL or from a passed string
     *
     * @param {string} hash
     * @param {boolean} [sanitize=true] If true, returns hash without hash symbol
     * @returns {string}
     */
    parseHashFromString(hash = window.location.hash, sanitize = true) {
        if (hash && sanitize) {
            return hash.split('#')[1];
        }

        return hash;
    },

    /**
     * Update browser's URL without page reload
     *
     * @param {string} newUrl
     */
    setLocation(newUrl) {
        if (window.history.pushState) {
            window.history.pushState(null, null, newUrl);
        }
    },

    replaceURL(inputUrl) {
        const { origin, pathname } = window.location;

        window.history.replaceState(
            {
                as: `${origin}${pathname}${inputUrl}`,
                options: {},
                url: inputUrl
            },
            '',
            `${origin}${pathname}${inputUrl}`
        );
    },

    /**
     * Parse URL
     *
     * Docs: https://www.npmjs.com/package/url#urlparseurlstr-parsequerystring-slashesdenotehost
     *
     */
    parse(urlString = window.location.href) {
        return url.parse(urlString, true);
    },

    /**
     * Parse URL pathname without any parameters, either from the browser's current URL or from a passed string
     *
     * Docs: https://www.npmjs.com/package/url#urlparseurlstr-parsequerystring-slashesdenotehost
     *
     * @param {string} urlString 'example.com/test?a=valueA&b=valueB'
     * @return {string} example.com/test
     */
    parsePathname(urlString = window.location.href) {
        return urlString ? url.parse(urlString, true).pathname || '' : '';
    },

    /**
     * Parse URL parameters, either from the browser's current URL or from a passed string
     *
     * Docs: https://www.npmjs.com/package/url#urlparseurlstr-parsequerystring-slashesdenotehost
     *
     * @param {string} urlString 'example.com/test?a=valueA&b=valueB'
     * @return {Object} { a: 'valueA', b: 'valueB' }
     */
    getParams(urlString = window.location.search) {
        return urlString ? url.parse(urlString, true).query || {} : {};
    },

    /**
     * Update URL paramaters
     *
     * @param {Object} newParams Example: { paramA: 'valueA', paramB: 'valueB' }
     * @param {Boolean} merge  if true persist current URL params
     */
    updateParams(newParams, merge = true) {
        const prevParams = this.getParams();
        const nextParams = merge ? { ...prevParams, ...newParams } : newParams;

        // no change
        if (isEqual(prevParams, nextParams)) {
            return;
        }

        const updatedParams = this.format({
            query: nextParams
        });

        if (window.history.pushState) {
            window.history.pushState(null, null, updatedParams);
        }
    },

    /**
     * Remove URL param(s) by name(s)
     *
     * @param {String|Array} paramNames (ie: 'paramA' or ['paramA', 'paramB'])
     */
    removeParams(paramNames) {
        const paramsArray = Array.isArray(paramNames)
            ? paramNames
            : [paramNames];

        if (!paramsArray.length) {
            return;
        }

        const prevParams = this.getParams();
        const prevParamsArray = Object.keys(prevParams);
        let updatedParams;

        // remove param one by one
        const filteredParams = prevParamsArray.filter(
            param => !paramsArray.includes(param)
        );

        // no change needed
        if (filteredParams.length === prevParamsArray.length) {
            return;
        }

        if (filteredParams.length) {
            // update URL with the remaining params
            const query = {};
            filteredParams.forEach(param => {
                query[param] = prevParams[param];
            });

            updatedParams = this.format({
                query
            });
        } else {
            // if no params are left, remove search query from URL
            updatedParams = window.location.pathname;
        }

        // update UI
        if (window.history.pushState) {
            window.history.pushState(null, '', updatedParams);
        }
    },

    /**
     * Take a parsed URL object, and return a formatted URL string.
     *
     * Docs: https://www.npmjs.com/package/url#urlformaturlobj
     *
     * @param {Object} config { pathname: 'example.com/test', query: { key: 'keyValue', ...}}
     * @return {String} 'example.com/test?key=keyValue'
     */
    format(config) {
        return url.format(config);
    },

    /**
     * Normalize encoding of special characters in URI (mainly because of IE)
     *
     * @param {String} string
     * @return {String}
     */
    encode(inputString) {
        return encodeURIComponent(inputString);
    },

    /**
     * Normalize decoding of special characters in URI (mainly because of IE)
     *
     * @param {String} string
     * @return {String}
     */
    decode(inputString) {
        return decodeURIComponent(inputString);
    },

    /**
     * Normalize decoding of array in URI
     *
     * @param {String} string
     * @return {Array}
     */
    decodeArray(inputString) {
        return this.decode(inputString).split(VALUE_SEPARATOR);
    },

    /**
     * Normalize endocing of array in URI
     */
    encodeArray(arr) {
        return this.encode(arr.toString());
    },

    /**
     * Update parameter in URL, or remove it from URL if the value is empty
     */
    updateOrRemoveParameterWhenEmpty(parameter, value) {
        if (isset(value)) {
            this.updateParams({ [parameter]: this.encode(value.toString()) });
        } else {
            this.removeParams(parameter);
        }
    },

    redirect(nextUrl) {
        window.location.href = nextUrl;
    },

    async redirectWithDelay(nextUrl, delay = 200) {
        await wait(delay);

        this.redirect(nextUrl);
    },

    VALUE_SEPARATOR
};
